import { Building2 } from 'lucide-react';
import { useCompanies, useCompanySelectionGuard } from '../../hooks/useCompanies';
import { useCompanyStore } from '../../store/useCompanyStore';
import { useI18n } from '../../contexts/I18nContext';
import { Select } from '../ui/Select';

/**
 * Global firm switcher (multi-company). Lives in the sidebar under the brand
 * block. Selecting "Toate firmele" (value null) shows aggregated data; picking
 * a firm scopes every companyId-threaded query (dashboard, projects, clients,
 * suppliers, invoices, smartbill, bank statements).
 *
 * Invariant: with a single firm the app must look exactly like before, so the
 * switcher renders nothing when there are ≤1 firms.
 */
export function CompanySwitcher() {
  const { t } = useI18n();
  const { data: companies } = useCompanies();
  const activeCompanyId = useCompanyStore((s) => s.activeCompanyId);
  const setActiveCompanyId = useCompanyStore((s) => s.setActiveCompanyId);

  // Reset a stale persisted selection (deleted firm) back to "all firms".
  useCompanySelectionGuard();

  if (!companies || companies.length <= 1) return null;

  const options = [
    { value: '', label: t('company.switcher.all') },
    ...companies.map((c) => ({
      value: c.id,
      label: c.displayName || c.legalName,
    })),
  ];

  return (
    <div className="px-4 pb-2">
      <div className="flex items-center gap-2">
        <Building2 size={16} className="shrink-0 text-muted-foreground" />
        <Select
          label={t('company.switcher.label')}
          hideLabel
          size="sm"
          value={activeCompanyId ?? ''}
          onChange={(value) => setActiveCompanyId(value || null)}
          options={options}
          className="min-w-0 flex-1"
        />
      </div>
    </div>
  );
}
